// LayoutEditor.tsx - VERSÃO APRIMORADA
import React, { useState, useEffect } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/LayoutEditor";

interface LayoutEditorProps {
  ambient: any;
  onSave: (layoutData: any) => void;
  onClose: () => void;
}

const LayoutEditor: React.FC<LayoutEditorProps> = ({
  ambient,
  onSave,
  onClose,
}) => {
  const [layout, setLayout] = useState(
    ambient.layout || {
      layoutUuid: "",
      layoutName: "",
      wallColor: "#f8f9fa",
      floorType: "Porcelanato",
      floorColor: "#e9ecef",
      lighting: "LED 3000K",
      tables: 0,
      chairs: 0,
      capacity: 0,
      features: [],
      // NOVOS CAMPOS para experiência mobile
      ambientImage: "", // Foto real do ambiente
      viewAngle: "front", // front, side, top, panoramic
      navigationPoints: [], // Pontos de interesse para navegação
      adjacentAmbients: [], // Ambientes conectados
      atmosphere: {
        musicType: "Ambiente",
        temperature: "Climatizado",
        lighting: "Natural + Artificial",
        noise: "Moderado",
      },
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState("");
  const [previewMode, setPreviewMode] = useState<"2d" | "3d">("2d");

  const floorTypes = [
    "Porcelanato",
    "Madeira",
    "Carpete",
    "Epóxi",
    "Antiderrapante",
    "Cimento queimado",
    "Piso Vinílico",
    "Mármore",
    "Granito",
  ];

  const lightingOptions = [
    "LED 3000K (Quente)",
    "LED 4000K (Neutro)",
    "LED 5000K (Frio)",
    "Luz Natural",
    "Incandescente",
    "Fluorescente",
    "Luz Decorativa",
  ];

  const availableFeatures = [
    "Wi-Fi Gratuito",
    "Ar-condicionado",
    "Som ambiente",
    "TV",
    "Projetor",
    "Tomadas nas mesas",
    "Acessibilidade",
    "Pet-friendly",
    "Área externa",
    "Vista panorâmica",
    "Jardim",
    "Lareira",
    "Bar integrado",
    "Buffet",
  ];

  const musicTypes = [
    "Ambiente",
    "Jazz",
    "MPB",
    "Pop",
    "Rock",
    "Clássica",
    "Eletrônica",
    "Sem música",
  ];

  useEffect(() => {
    // Auto-calcular capacidade baseado em mesas/cadeiras
    const estimatedCapacity = layout.chairs || layout.tables * 4;
    if (layout.capacity === 0 && estimatedCapacity > 0) {
      setLayout((prev: any) => ({ ...prev, capacity: estimatedCapacity }));
    }
  }, [layout.tables, layout.chairs]);

  const handleAddFeature = () => {
    if (selectedFeature && !layout.features.includes(selectedFeature)) {
      setLayout({
        ...layout,
        features: [...layout.features, selectedFeature],
      });
      setSelectedFeature("");
    }
  };

  const handleRemoveFeature = (feature: string) => {
    setLayout({
      ...layout,
      features: layout.features.filter((f: string) => f !== feature),
    });
  };

  const handleSave = async () => {
    if (!layout.layoutName.trim()) {
      alert("Por favor, dê um nome ao layout");
      return;
    }

    setIsSaving(true);
    try {
      await onSave(layout);
    } finally {
      setIsSaving(false);
    }
  };

  // Renderiza preview 2D
  const render2DPreview = () => (
    <AlfredoStyles.Preview2D
      $wallColor={layout.wallColor}
      $floorColor={layout.floorColor}
    >
      <AlfredoStyles.TablesContainer>
        {Array.from({ length: Math.min(layout.tables, 16) }).map((_, i) => (
          <AlfredoStyles.TableItem key={i}>
            <AlfredoStyles.TableTop />
            <AlfredoStyles.TableNumber>{i + 1}</AlfredoStyles.TableNumber>
          </AlfredoStyles.TableItem>
        ))}
      </AlfredoStyles.TablesContainer>

      {/* Indicadores de features */}
      <AlfredoStyles.FeaturesOverlay>
        {layout.features.includes("Wi-Fi Gratuito") && (
          <AlfredoStyles.FeatureIcon title="Wi-Fi">
            📶
          </AlfredoStyles.FeatureIcon>
        )}
        {layout.features.includes("Ar-condicionado") && (
          <AlfredoStyles.FeatureIcon title="Ar-condicionado">
            ❄️
          </AlfredoStyles.FeatureIcon>
        )}
        {layout.features.includes("Som ambiente") && (
          <AlfredoStyles.FeatureIcon title="Som">🎵</AlfredoStyles.FeatureIcon>
        )}
      </AlfredoStyles.FeaturesOverlay>
    </AlfredoStyles.Preview2D>
  );

  // Renderiza preview 3D simplificado
  const render3DPreview = () => (
    <AlfredoStyles.Preview3D>
      <AlfredoStyles.Room3D $wallColor={layout.wallColor}>
        {/* Parede frontal */}
        <AlfredoStyles.Wall3D $position="front" />

        {/* Piso */}
        <AlfredoStyles.Floor3D $floorColor={layout.floorColor} />

        {/* Mesas em perspectiva */}
        <AlfredoStyles.Tables3DContainer>
          {Array.from({ length: Math.min(layout.tables, 8) }).map((_, i) => (
            <AlfredoStyles.Table3D key={i} $position={i}>
              <AlfredoStyles.TableTop3D />
            </AlfredoStyles.Table3D>
          ))}
        </AlfredoStyles.Tables3DContainer>
      </AlfredoStyles.Room3D>

      <AlfredoStyles.View3DInfo>
        Vista 3D Simplificada - Preview mobile
      </AlfredoStyles.View3DInfo>
    </AlfredoStyles.Preview3D>
  );

  return (
    <AlfredoStyles.EditorContainer>
      <AlfredoStyles.EditorHeader>
        <AlfredoStyles.HeaderLeft>
          <AlfredoStyles.MaterialIcons>
            design_services
          </AlfredoStyles.MaterialIcons>
          <div>
            <AlfredoStyles.EditorTitle>
              Editor de Layout
            </AlfredoStyles.EditorTitle>
            <AlfredoStyles.EditorSubtitle>
              {ambient.ambientName}
            </AlfredoStyles.EditorSubtitle>
          </div>
        </AlfredoStyles.HeaderLeft>

        <AlfredoStyles.CloseButton onClick={onClose}>
          <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
        </AlfredoStyles.CloseButton>
      </AlfredoStyles.EditorHeader>

      <AlfredoStyles.EditorContent>
        {/* Coluna Esquerda - Preview */}
        <AlfredoStyles.PreviewColumn>
          <AlfredoStyles.PreviewHeader>
            <AlfredoStyles.SectionTitle>
              Preview do Ambiente
            </AlfredoStyles.SectionTitle>

            <AlfredoStyles.ViewToggle>
              <AlfredoStyles.ViewButton
                $isActive={previewMode === "2d"}
                onClick={() => setPreviewMode("2d")}
              >
                <AlfredoStyles.MaterialIcons>
                  grid_view
                </AlfredoStyles.MaterialIcons>
                2D
              </AlfredoStyles.ViewButton>
              <AlfredoStyles.ViewButton
                $isActive={previewMode === "3d"}
                onClick={() => setPreviewMode("3d")}
              >
                <AlfredoStyles.MaterialIcons>
                  view_in_ar
                </AlfredoStyles.MaterialIcons>
                3D
              </AlfredoStyles.ViewButton>
            </AlfredoStyles.ViewToggle>
          </AlfredoStyles.PreviewHeader>

          {previewMode === "2d" ? render2DPreview() : render3DPreview()}

          {/* Métricas Rápidas */}
          <AlfredoStyles.MetricsQuick>
            <AlfredoStyles.MetricQuickItem>
              <AlfredoStyles.MaterialIcons>
                table_restaurant
              </AlfredoStyles.MaterialIcons>
              <div>
                <AlfredoStyles.MetricValue>
                  {layout.tables}
                </AlfredoStyles.MetricValue>
                <AlfredoStyles.MetricLabel>Mesas</AlfredoStyles.MetricLabel>
              </div>
            </AlfredoStyles.MetricQuickItem>

            <AlfredoStyles.MetricQuickItem>
              <AlfredoStyles.MaterialIcons>chair</AlfredoStyles.MaterialIcons>
              <div>
                <AlfredoStyles.MetricValue>
                  {layout.chairs}
                </AlfredoStyles.MetricValue>
                <AlfredoStyles.MetricLabel>Cadeiras</AlfredoStyles.MetricLabel>
              </div>
            </AlfredoStyles.MetricQuickItem>

            <AlfredoStyles.MetricQuickItem>
              <AlfredoStyles.MaterialIcons>people</AlfredoStyles.MaterialIcons>
              <div>
                <AlfredoStyles.MetricValue>
                  {layout.capacity}
                </AlfredoStyles.MetricValue>
                <AlfredoStyles.MetricLabel>
                  Capacidade
                </AlfredoStyles.MetricLabel>
              </div>
            </AlfredoStyles.MetricQuickItem>

            <AlfredoStyles.MetricQuickItem>
              <AlfredoStyles.MaterialIcons>star</AlfredoStyles.MaterialIcons>
              <div>
                <AlfredoStyles.MetricValue>
                  {layout.features.length}
                </AlfredoStyles.MetricValue>
                <AlfredoStyles.MetricLabel>Features</AlfredoStyles.MetricLabel>
              </div>
            </AlfredoStyles.MetricQuickItem>
          </AlfredoStyles.MetricsQuick>
        </AlfredoStyles.PreviewColumn>

        {/* Coluna Direita - Configurações */}
        <AlfredoStyles.ConfigColumn>
          <AlfredoStyles.ScrollableContent>
            {/* Configurações Básicas */}
            <AlfredoStyles.ConfigSection>
              <AlfredoStyles.SectionTitle>
                <AlfredoStyles.MaterialIcons>tune</AlfredoStyles.MaterialIcons>
                Configurações Básicas
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label $required>
                  Nome do Layout
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="text"
                  value={layout.layoutName}
                  onChange={(e) =>
                    setLayout({ ...layout, layoutName: e.target.value })
                  }
                  placeholder="Ex: Layout Moderno, Layout Rústico..."
                />
                <AlfredoStyles.Hint>
                  Escolha um nome descritivo para identificar este layout
                </AlfredoStyles.Hint>
              </AlfredoStyles.FormGroup>

              <AlfredoStyles.FormRow>
                <AlfredoStyles.FormGroup>
                  <AlfredoStyles.Label>Cor das Paredes</AlfredoStyles.Label>
                  <AlfredoStyles.ColorPickerWrapper>
                    <AlfredoStyles.ColorInput
                      type="color"
                      value={layout.wallColor}
                      onChange={(e) =>
                        setLayout({ ...layout, wallColor: e.target.value })
                      }
                    />
                    <AlfredoStyles.ColorValue>
                      {layout.wallColor}
                    </AlfredoStyles.ColorValue>
                  </AlfredoStyles.ColorPickerWrapper>
                </AlfredoStyles.FormGroup>

                <AlfredoStyles.FormGroup>
                  <AlfredoStyles.Label>Cor do Piso</AlfredoStyles.Label>
                  <AlfredoStyles.ColorPickerWrapper>
                    <AlfredoStyles.ColorInput
                      type="color"
                      value={layout.floorColor}
                      onChange={(e) =>
                        setLayout({ ...layout, floorColor: e.target.value })
                      }
                    />
                    <AlfredoStyles.ColorValue>
                      {layout.floorColor}
                    </AlfredoStyles.ColorValue>
                  </AlfredoStyles.ColorPickerWrapper>
                </AlfredoStyles.FormGroup>
              </AlfredoStyles.FormRow>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label>Tipo de Piso</AlfredoStyles.Label>
                <AlfredoStyles.Select
                  value={layout.floorType}
                  onChange={(e) =>
                    setLayout({ ...layout, floorType: e.target.value })
                  }
                >
                  {floorTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </AlfredoStyles.Select>
              </AlfredoStyles.FormGroup>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label>Iluminação</AlfredoStyles.Label>
                <AlfredoStyles.Select
                  value={layout.lighting}
                  onChange={(e) =>
                    setLayout({ ...layout, lighting: e.target.value })
                  }
                >
                  {lightingOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </AlfredoStyles.Select>
              </AlfredoStyles.FormGroup>
            </AlfredoStyles.ConfigSection>

            {/* Mobiliário */}
            <AlfredoStyles.ConfigSection>
              <AlfredoStyles.SectionTitle>
                <AlfredoStyles.MaterialIcons>
                  table_restaurant
                </AlfredoStyles.MaterialIcons>
                Mobiliário
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormRow>
                <AlfredoStyles.FormGroup>
                  <AlfredoStyles.Label>Número de Mesas</AlfredoStyles.Label>
                  <AlfredoStyles.NumberInput
                    type="number"
                    value={layout.tables}
                    onChange={(e) =>
                      setLayout({
                        ...layout,
                        tables: parseInt(e.target.value) || 0,
                      })
                    }
                    min="0"
                    max="50"
                  />
                </AlfredoStyles.FormGroup>

                <AlfredoStyles.FormGroup>
                  <AlfredoStyles.Label>Número de Cadeiras</AlfredoStyles.Label>
                  <AlfredoStyles.NumberInput
                    type="number"
                    value={layout.chairs}
                    onChange={(e) =>
                      setLayout({
                        ...layout,
                        chairs: parseInt(e.target.value) || 0,
                      })
                    }
                    min="0"
                    max="200"
                  />
                </AlfredoStyles.FormGroup>
              </AlfredoStyles.FormRow>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label>
                  Capacidade Total (pessoas)
                </AlfredoStyles.Label>
                <AlfredoStyles.NumberInput
                  type="number"
                  value={layout.capacity}
                  onChange={(e) =>
                    setLayout({
                      ...layout,
                      capacity: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                  max="500"
                />
                <AlfredoStyles.Hint>
                  💡 Sugestão: {layout.chairs || layout.tables * 4} pessoas
                  baseado nas cadeiras/mesas
                </AlfredoStyles.Hint>
              </AlfredoStyles.FormGroup>
            </AlfredoStyles.ConfigSection>

            {/* Características */}
            <AlfredoStyles.ConfigSection>
              <AlfredoStyles.SectionTitle>
                <AlfredoStyles.MaterialIcons>star</AlfredoStyles.MaterialIcons>
                Características e Diferenciais
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label>
                  Adicionar Característica
                </AlfredoStyles.Label>
                <AlfredoStyles.AddFeatureRow>
                  <AlfredoStyles.Select
                    value={selectedFeature}
                    onChange={(e) => setSelectedFeature(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {availableFeatures.map((feature) => (
                      <option
                        key={feature}
                        value={feature}
                        disabled={layout.features.includes(feature)}
                      >
                        {feature}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                  <AlfredoStyles.AddButton
                    onClick={handleAddFeature}
                    disabled={!selectedFeature}
                  >
                    <AlfredoStyles.MaterialIcons>
                      add
                    </AlfredoStyles.MaterialIcons>
                  </AlfredoStyles.AddButton>
                </AlfredoStyles.AddFeatureRow>
              </AlfredoStyles.FormGroup>

              {layout.features.length > 0 && (
                <AlfredoStyles.FeaturesGrid>
                  {layout.features.map((feature: string, index: number) => (
                    <AlfredoStyles.FeatureTag key={index}>
                      <span>{feature}</span>
                      <AlfredoStyles.RemoveFeatureButton
                        onClick={() => handleRemoveFeature(feature)}
                      >
                        <AlfredoStyles.MaterialIcons>
                          close
                        </AlfredoStyles.MaterialIcons>
                      </AlfredoStyles.RemoveFeatureButton>
                    </AlfredoStyles.FeatureTag>
                  ))}
                </AlfredoStyles.FeaturesGrid>
              )}
            </AlfredoStyles.ConfigSection>

            {/* Atmosfera (NOVO!) */}
            <AlfredoStyles.ConfigSection>
              <AlfredoStyles.SectionTitle>
                <AlfredoStyles.MaterialIcons>mood</AlfredoStyles.MaterialIcons>
                Atmosfera e Ambiente
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGroup>
                <AlfredoStyles.Label>Tipo de Música</AlfredoStyles.Label>
                <AlfredoStyles.Select
                  value={layout.atmosphere?.musicType || "Ambiente"}
                  onChange={(e) =>
                    setLayout({
                      ...layout,
                      atmosphere: {
                        ...layout.atmosphere,
                        musicType: e.target.value,
                      },
                    })
                  }
                >
                  {musicTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </AlfredoStyles.Select>
              </AlfredoStyles.FormGroup>

              <AlfredoStyles.AtmosphereGrid>
                <AlfredoStyles.AtmosphereItem>
                  <AlfredoStyles.MaterialIcons>
                    thermostat
                  </AlfredoStyles.MaterialIcons>
                  <span>Climatizado</span>
                </AlfredoStyles.AtmosphereItem>
                <AlfredoStyles.AtmosphereItem>
                  <AlfredoStyles.MaterialIcons>
                    light_mode
                  </AlfredoStyles.MaterialIcons>
                  <span>Iluminado</span>
                </AlfredoStyles.AtmosphereItem>
                <AlfredoStyles.AtmosphereItem>
                  <AlfredoStyles.MaterialIcons>
                    volume_up
                  </AlfredoStyles.MaterialIcons>
                  <span>Som ambiente</span>
                </AlfredoStyles.AtmosphereItem>
              </AlfredoStyles.AtmosphereGrid>
            </AlfredoStyles.ConfigSection>
          </AlfredoStyles.ScrollableContent>
        </AlfredoStyles.ConfigColumn>
      </AlfredoStyles.EditorContent>

      {/* Footer com ações */}
      <AlfredoStyles.EditorFooter>
        <AlfredoStyles.FooterInfo>
          <AlfredoStyles.MaterialIcons>info</AlfredoStyles.MaterialIcons>
          Este layout será usado no app mobile para navegação virtual
        </AlfredoStyles.FooterInfo>

        <AlfredoStyles.FooterActions>
          <AlfredoStyles.SecondaryButton onClick={onClose} disabled={isSaving}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
            Cancelar
          </AlfredoStyles.SecondaryButton>
          <AlfredoStyles.PrimaryButton
            onClick={handleSave}
            disabled={isSaving || !layout.layoutName.trim()}
          >
            {isSaving ? (
              <>
                <AlfredoStyles.ButtonSpinner />
                Salvando...
              </>
            ) : (
              <>
                <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
                Salvar Layout
              </>
            )}
          </AlfredoStyles.PrimaryButton>
        </AlfredoStyles.FooterActions>
      </AlfredoStyles.EditorFooter>
    </AlfredoStyles.EditorContainer>
  );
};

export default LayoutEditor;
